/**
 * Best available (docs/inventory.md §5.3): pick `quantity` seats together,
 * from the best group, nearest the middle of it. Pure: the Durable Object
 * hands in the seats that are actually free right now and holds whatever comes
 * back, inside the same transaction, so the answer cannot go stale between
 * choosing and holding.
 *
 * "Together" means one **group**, and a group is a row *or a table* (D-146).
 * A cabaret room has no rows at all, so grouping by row alone did not merely
 * rank tables badly — it excluded every table seat, and best available
 * answered "no block of that many seats is available" with the room empty.
 *
 * The two kinds of group seat a party differently, and the code says so:
 *
 *   - A **row** is a line. Seats are together only where their sort orders run
 *     consecutively, and a block nearer the row's middle wins.
 *   - A **table** is a circle. Everyone at it is together whichever chairs
 *     they take, so any `quantity` of its free seats will do; a consecutive
 *     run is preferred where one exists, purely so that a couple sits side by
 *     side. Among equally ranked tables the **fullest table that still fits**
 *     wins, which fills a table before opening a new one instead of leaving
 *     thirty tables with two people at each.
 *
 * Groups rank by `group_rank` (lower is better — the Durable Object derives it
 * from **level**, then section, then row sort order, front to back; D-108), so
 * a floor seat beats a balcony seat however the sections are numbered. A
 * manifest gives tables no sort order of their own, so tables in one section
 * all share a rank; their tie breaks on the printed label read the way a
 * person reads it, table 2 before table 10.
 *
 * Venues that want a different notion of "best" order their levels, sections
 * and table labels; the code stays the same.
 *
 * A table marked `WHOLE_TABLE` is all of it or none of it (D-152), so best
 * available offers one only to a party that is exactly its size, and never
 * breaks one up to fill a split.
 */

/** A row seats a party in a line; a table seats it in a circle. */
export type SeatGroupKind = 'ROW' | 'TABLE';

export interface CandidateSeat {
  unit_id: string;
  /** The row or table this seat belongs to: the unit of "together". */
  group_id: string;
  group_kind: SeatGroupKind;
  /** The group's printed label ('F', '12'), used to break rank ties. */
  group_label: string | null;
  /** Lower is better. Ties broken by fit, then label. */
  group_rank: number;
  /** Physical order along the row or around the table (seats.sort_order). */
  seat_sort_order: number;
  /** True when this is a table sold whole: all of it or none of it (D-152). */
  group_whole?: boolean;
  /** Seats in the group in total, free or not. Only read for a whole table. */
  group_size?: number;
  price_zone_id: string | null;
  section_id: string | null;
}

export interface BestAvailableOptions {
  /** Restrict to these sections, when given. */
  section_ids?: readonly string[] | undefined;
  /** Restrict to these price zones, when given. */
  price_zone_ids?: readonly string[] | undefined;
  /** Accept seats split across groups when no one group can seat the party (default false). */
  allow_split?: boolean | undefined;
}

export interface BestAvailableChoice {
  unit_ids: string[];
  /** True when the party is seated together in one row or at one table. */
  together: boolean;
}

/**
 * Compares labels the way a person reads them, so table 2 comes before table
 * 10 and row A before row B. Hand-rolled rather than `localeCompare` with
 * `numeric: true` so that the order is identical everywhere this runs,
 * including inside a Durable Object, and never depends on an ICU build.
 */
export function compareLabels(a: string, b: string): number {
  const parts = /\d+|\D+/g;
  const left = a.match(parts) ?? [];
  const right = b.match(parts) ?? [];
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const x = left[i];
    const y = right[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    if (x === y) continue;
    if (/^\d/.test(x) && /^\d/.test(y)) {
      if (Number(x) !== Number(y)) return Number(x) - Number(y);
      continue;
    }
    return x < y ? -1 : 1;
  }
  return 0;
}

interface Block {
  seats: CandidateSeat[];
  rank: number;
  /**
   * Lower fits better. For a row it is how far the block sits from the middle
   * of what is free; for a table it is how many seats the table has free, so
   * that a table already in use is filled before an empty one is opened. The
   * two meanings are only ever compared against their own kind: a section is
   * rows or tables, never both, and `rank` separates sections.
   */
  fit: number;
  /** Where the block starts, so the same cart always gets the same seats. */
  start: number;
  label: string;
}

const isConsecutive = (block: readonly CandidateSeat[]): boolean =>
  block.every((seat, i) => i === 0 || seat.seat_sort_order === block[i - 1]!.seat_sort_order + 1);

/** The first run of `quantity` seats whose sort orders are consecutive, if any. */
function consecutiveRun(ordered: readonly CandidateSeat[], quantity: number): CandidateSeat[] | null {
  for (let start = 0; start + quantity <= ordered.length; start += 1) {
    const run = ordered.slice(start, start + quantity);
    if (isConsecutive(run)) return run;
  }
  return null;
}

function better(candidate: Block, incumbent: Block): boolean {
  if (candidate.rank !== incumbent.rank) return candidate.rank < incumbent.rank;
  if (candidate.fit !== incumbent.fit) return candidate.fit < incumbent.fit;
  if (candidate.start !== incumbent.start) return candidate.start < incumbent.start;
  const byLabel = compareLabels(candidate.label, incumbent.label);
  if (byLabel !== 0) return byLabel < 0;
  // Nothing left to prefer: settle it on identity, so the same free room always
  // answers the same way whatever order the seats arrived in.
  return candidate.seats[0]!.unit_id < incumbent.seats[0]!.unit_id;
}

export function chooseBestAvailable(
  candidates: readonly CandidateSeat[],
  quantity: number,
  options: BestAvailableOptions = {},
): BestAvailableChoice | null {
  if (!Number.isInteger(quantity) || quantity < 1) return null;
  const sections = options.section_ids?.length ? new Set(options.section_ids) : null;
  const zones = options.price_zone_ids?.length ? new Set(options.price_zone_ids) : null;
  const eligible = candidates.filter(
    (seat) => (!sections || (seat.section_id !== null && sections.has(seat.section_id))) && (!zones || (seat.price_zone_id !== null && zones.has(seat.price_zone_id))),
  );
  if (eligible.length < quantity) return null;

  const groups = new Map<string, CandidateSeat[]>();
  for (const seat of eligible) groups.set(seat.group_id, [...(groups.get(seat.group_id) ?? []), seat]);

  let best: Block | null = null;
  for (const seats of groups.values()) {
    const ordered = [...seats].sort((a, b) => a.seat_sort_order - b.seat_sort_order);
    if (ordered.length < quantity) continue;
    const first = ordered[0]!;
    const label = first.group_label ?? first.group_id;

    if (first.group_kind === 'TABLE') {
      /*
       * A table sold whole is all of it or none of it (D-152), so it is a
       * candidate only when the party is exactly its size and every seat is
       * on offer. Handing two people a six-top would charge them for six;
       * handing six people four free chairs of a six-top would sell part of
       * something that is not sold in parts.
       */
      if (first.group_whole && (quantity !== (first.group_size ?? ordered.length) || ordered.length !== (first.group_size ?? ordered.length))) continue;
      // Any chairs at the table are together; a run is preferred only so that
      // a party sits beside itself.
      const run = consecutiveRun(ordered, quantity) ?? ordered.slice(0, quantity);
      const block: Block = { seats: run, rank: first.group_rank, fit: ordered.length, start: run[0]!.seat_sort_order, label };
      if (!best || better(block, best)) best = block;
      continue;
    }

    // A row's center is estimated from what is on offer: the midpoint of its
    // lowest and highest free sort orders. Good enough to prefer middle over
    // ends without knowing the full row.
    const center = (ordered[0]!.seat_sort_order + ordered[ordered.length - 1]!.seat_sort_order) / 2;
    for (let start = 0; start + quantity <= ordered.length; start += 1) {
      const run = ordered.slice(start, start + quantity);
      if (!isConsecutive(run)) continue;
      const runCenter = (run[0]!.seat_sort_order + run[run.length - 1]!.seat_sort_order) / 2;
      const block: Block = { seats: run, rank: first.group_rank, fit: Math.abs(runCenter - center), start: run[0]!.seat_sort_order, label };
      if (!best || better(block, best)) best = block;
    }
  }
  if (best) return { unit_ids: best.seats.map((seat) => seat.unit_id), together: true };

  if (!options.allow_split) return null;
  // Split: best seats individually, front to back, center out — and never a
  // chair from a table that is sold whole, which cannot be broken up (D-152).
  const scored = eligible
    .filter((seat) => !seat.group_whole)
    .map((seat) => ({ seat, score: seat.group_rank * 1_000_000 + seat.seat_sort_order }))
    .sort((a, b) => a.score - b.score || (a.seat.unit_id < b.seat.unit_id ? -1 : 1))
    .slice(0, quantity);
  // Filtering whole tables out can leave fewer seats than were asked for, and a
  // short answer would hold two seats for a party of three and call it done.
  if (scored.length < quantity) return null;
  return { unit_ids: scored.map((s) => s.seat.unit_id), together: false };
}
